/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Conversational Chat & Sessions Store (Task 10.3.1 & Task 10.3.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';
import { SYSTEM_CHAT_PERSONAS, CHAT_AI_MODELS } from '../data/chat-personas.catalog';
import type { ChatAttachmentItem } from '../services/chat-attachment.service';

export interface ChatSessionItem {
  id: string;
  title: string;
  personaId: string;
  modelCode: string;
  isPinned: boolean;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  totalMessages: number;
}

export interface ChatCitationItem {
  id?: string;
  title?: string;
  fileName?: string;
  sourceUrl?: string;
  page?: number;
  snippet: string;
  score?: number;
}

export interface ChatMessageItem {
  id: string;
  sessionId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  attachments?: ChatAttachmentItem[];
  citations?: ChatCitationItem[];
  tokensUsed?: number;
  creditCost?: number;
  modelCode?: string;
  personaId?: string;
  isStreaming?: boolean;
  isError?: boolean;
  feedback?: 'like' | 'dislike';
  feedbackReason?: string;
  createdAt: string;
}

export interface GroupedSessions {
  pinned: ChatSessionItem[];
  today: ChatSessionItem[];
  yesterday: ChatSessionItem[];
  last7Days: ChatSessionItem[];
  older: ChatSessionItem[];
}

export interface ChatState {
  sessions: ChatSessionItem[];
  messages: Record<string, ChatMessageItem[]>;
  activeSessionId: string | null;
  activePersonaId: string;
  activeModelCode: string;
  searchQuery: string;
  isSidebarOpen: boolean;
  isGenerating: boolean;
  activeCitationDrawer: {
    messageId: string;
    selectedIndex: number;
    citations: ChatCitationItem[];
  } | null;
  isShareModalOpen: boolean;
  shareModalSessionId: string | null;

  // Session Actions
  createSession: (options?: { title?: string; personaId?: string; modelCode?: string }) => string;
  selectSession: (id: string) => void;
  updateSessionTitle: (id: string, title: string) => void;
  togglePinSession: (id: string) => void;
  deleteSession: (id: string) => void;
  incrementSessionMessages: (id: string) => void;
  setActivePersona: (personaId: string) => void;
  setActiveModel: (modelCode: string) => void;
  setSearchQuery: (query: string) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  resetStore: () => void;

  // Message Actions
  addMessage: (
    sessionId: string,
    message: Omit<ChatMessageItem, 'id' | 'sessionId' | 'createdAt'> & {
      id?: string;
      sessionId?: string;
      createdAt?: string;
    }
  ) => string;
  updateMessageContent: (sessionId: string, messageId: string, content: string, isStreaming?: boolean) => void;
  appendMessageDelta: (sessionId: string, messageId: string, delta: string) => void;
  finalizeMessage: (
    sessionId: string,
    messageId: string,
    meta?: {
      isStreaming?: boolean;
      tokensUsed?: number;
      creditCost?: number;
      citations?: ChatCitationItem[];
    }
  ) => void;
  rateMessage: (
    sessionId: string,
    messageId: string,
    rating: 'like' | 'dislike' | null,
    reason?: string
  ) => void;
  clearSessionMessages: (sessionId: string) => void;

  // Drawer & Modal Actions
  openCitationDrawer: (messageId: string, selectedIndex: number, citations: ChatCitationItem[]) => void;
  closeCitationDrawer: () => void;
  openShareModal: (sessionId?: string) => void;
  closeShareModal: () => void;

  // Selectors
  getActiveSession: () => ChatSessionItem | undefined;
  getSessionMessages: (sessionId?: string | null) => ChatMessageItem[];
  getGroupedSessions: () => GroupedSessions;
}

/**
 * Helper to group sessions by relative time markers and pinned status.
 */
export function groupSessionsByTime(
  sessions: ChatSessionItem[],
  searchQuery: string = '',
  referenceDate: Date = new Date()
): GroupedSessions {
  const query = searchQuery.trim().toLowerCase();
  const filtered = query
    ? sessions.filter((s) => s.title.toLowerCase().includes(query))
    : [...sessions];

  const startOfToday = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  ).getTime();

  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfLast7Days = startOfToday - 7 * 24 * 60 * 60 * 1000;

  const result: GroupedSessions = {
    pinned: [],
    today: [],
    yesterday: [],
    last7Days: [],
    older: [],
  };

  for (const session of filtered) {
    if (session.isPinned) {
      result.pinned.push(session);
      continue;
    }

    const updatedTime = new Date(session.updatedAt).getTime();

    if (updatedTime >= startOfToday) {
      result.today.push(session);
    } else if (updatedTime >= startOfYesterday) {
      result.yesterday.push(session);
    } else if (updatedTime >= startOfLast7Days) {
      result.last7Days.push(session);
    } else {
      result.older.push(session);
    }
  }

  const sortByUpdatedDesc = (a: ChatSessionItem, b: ChatSessionItem) =>
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

  result.pinned.sort(sortByUpdatedDesc);
  result.today.sort(sortByUpdatedDesc);
  result.yesterday.sort(sortByUpdatedDesc);
  result.last7Days.sort(sortByUpdatedDesc);
  result.older.sort(sortByUpdatedDesc);

  return result;
}

const STORAGE_KEY_SESSIONS = 'sophia_chat_sessions';
const STORAGE_KEY_ACTIVE_ID = 'sophia_chat_active_session_id';
const STORAGE_KEY_PERSONA = 'sophia_chat_active_persona';
const STORAGE_KEY_MODEL = 'sophia_chat_active_model';
const STORAGE_KEY_MESSAGES = 'sophia_chat_messages';

const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Initial sample sessions for fresh user state.
 */
export const INITIAL_DEMO_SESSIONS: ChatSessionItem[] = [
  {
    id: 'session_demo_pinned',
    title: 'Chiến Lược Kiến Trúc Cloudflare Edge & D1',
    personaId: 'persona_engineer',
    modelCode: 'deepseek-r1',
    isPinned: true,
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    totalMessages: 2,
  },
  {
    id: 'session_demo_today',
    title: 'Kế Hoạch Khởi Chạy Sản Phẩm SaaS 2026',
    personaId: 'persona_sophia',
    modelCode: 'gpt-4o',
    isPinned: false,
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
    totalMessages: 2,
  },
  {
    id: 'session_demo_yesterday',
    title: 'Tối Ưu Bài Viết Trụ Cột SEO Công Thức PAS',
    personaId: 'persona_copywriter',
    modelCode: 'claude-3-5-sonnet',
    isPinned: false,
    createdAt: new Date(Date.now() - 28 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    totalMessages: 2,
  },
  {
    id: 'session_demo_lastweek',
    title: 'Phân Tích Cơ Chế Attention Transformer',
    personaId: 'persona_tutor',
    modelCode: 'gemini-1-5-pro',
    isPinned: false,
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    totalMessages: 2,
  },
];

export const INITIAL_DEMO_MESSAGES: Record<string, ChatMessageItem[]> = {
  session_demo_pinned: [
    {
      id: 'msg_pinned_01',
      sessionId: 'session_demo_pinned',
      role: 'user',
      content: 'Làm thế nào để thiết kế kiến trúc Serverless trên Cloudflare Edge nhằm đạt TTFT < 5ms và tối ưu chi phí lưu trữ D1 Database?',
      createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'msg_pinned_02',
      sessionId: 'session_demo_pinned',
      role: 'assistant',
      modelCode: 'deepseek-r1',
      tokensUsed: 340,
      citations: [
        {
          id: 'cit-doc-1',
          title: 'Cloudflare Edge Reference Architecture.pdf',
          sourceUrl: 'https://developers.cloudflare.com/reference-architecture',
          snippet: 'Edge KV and D1 offer sub-10ms query times globally when deployed on the Workers runtime.',
          score: 0.98,
        },
        {
          id: 'cit-doc-2',
          title: 'Sofia Platform LLM Vector Benchmarks 2026',
          sourceUrl: 'https://sofia.ai/benchmarks/edge-rag',
          snippet: 'Vectorize index query latency achieves P95 < 15ms with 1536d cosine similarity embeddings.',
          score: 0.94,
        },
      ],
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      content: `Để đạt **Time To First Token (TTFT) < 5ms** và kiểm soát chi phí tối ưu trên Cloudflare Edge, chúng ta áp dụng mô hình 3 tầng phân cấp:

### 1. Bảng So Sánh Các Lớp Lưu Trữ Edge

| Lớp Lưu Trữ | Công Nghệ Cloudflare | Độ Trễ (P95) | Tối Ưu Cho |
|---|---|---|---|
| **L1: Edge KV Cache** | Cloudflare KV | $< 2\\text{ms}$ | Cấu hình Tenant, Bể khóa KeyPool, Session Tokens |
| **L2: Relational DB** | Cloudflare D1 | $< 10\\text{ms}$ | Dữ liệu giao dịch, Ví Credits, Lịch sử trò chuyện |
| **L3: Vector Embeddings** | Vectorize | $< 15\\text{ms}$ | Vector 1536d, RAG Semantic Search |
| **L4: Object Storage** | Cloudflare R2 | $< 35\\text{ms}$ | Tệp Media, PDF, Audio Whisper ($0 egress) |

### 2. Triển Khai Edge Worker Mẫu

\`\`\`typescript
import { createDb } from '@sofia/db';

export async function handleStreamChat(req: Request, env: Env): Promise<Response> {
  const start = performance.now();
  const db = createDb(env.DB);

  // Khởi tạo luồng SSE trực tiếp tại điểm biên (Edge V8 Isolate)
  const stream = new TransformStream();
  const writer = stream.writable.getWriter();

  // Tính toán thời gian phản hồi:
  // $$T_{\\text{TTFT}} = T_{\\text{TLS}} + T_{\\text{IsolateBoot}} + T_{\\text{StreamStart}}$$
  return new Response(stream.readable, {
    headers: {
      'Content-Type': 'text/event-stream',
      'Cache-Control': 'no-cache',
    },
  });
}
\`\`\`

Công thức tính toán chi phí trung bình:
$$C_{\\text{total}} = \\sum_{i=1}^{n} (\\text{Tokens}_i \\times P_{\\text{model}}) + \\text{D1 Reads} \\times 0.001$$

Độ phức tạp truy xuất bộ nhớ đệm là $O(1)$ thông qua cơ chế KV Replication toàn cầu tại hơn 300+ PoPs.`,
    },
  ],
  session_demo_today: [
    {
      id: 'msg_today_01',
      sessionId: 'session_demo_today',
      role: 'user',
      content: 'Hãy giúp tôi lập kế hoạch 4 tuần để khởi chạy sản phẩm SaaS Sophia Platform ra mắt công chúng.',
      createdAt: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'msg_today_02',
      sessionId: 'session_demo_today',
      role: 'assistant',
      modelCode: 'gpt-4o',
      tokensUsed: 260,
      createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      content: `Chào bạn! Đây là lộ trình 4 tuần tinh gọn được thiết kế theo chuẩn Product-Led Growth (PLG):

- **Tuần 1: Chuẩn Bị & Private Beta**
  - Mời 50 người dùng dùng thử đầu tiên trải nghiệm Tiptap AI Writer và Chat Studio.
  - Thu thập feedback về độ mượt của luồng Server-Sent Events (SSE).
- **Tuần 2: SEO Hub & Landing Page**
  - Kích hoạt trang giới thiệu Astro 4 với điểm số Lighthouse 100/100.
  - Xuất bản 10 bài viết mẫu chuẩn SEO được tạo từ Article Wizard.
- **Tuần 3: Chiến Dịch Product Hunt & Community**
  - Chuẩn bị video demo 60 giây giới thiệu tính năng Voice STT và Multi-turn Chat.
  - Kích hoạt chương trình Tiếp thị liên kết (Affiliates 20% Lifetime Recurring).
- **Tuần 4: Public Launch & Scaling**
  - Mở cổng đăng ký tự do, tích hợp Stripe Billing và cổng thanh toán nội địa.
  - Giám sát Cloudflare Analytics Engine và độ ổn định của D1 Database.`,
    },
  ],
};

const loadStoredSessions = (): ChatSessionItem[] => {
  if (typeof window === 'undefined') return INITIAL_DEMO_SESSIONS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SESSIONS);
    if (!raw) return INITIAL_DEMO_SESSIONS;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : INITIAL_DEMO_SESSIONS;
  } catch {
    return INITIAL_DEMO_SESSIONS;
  }
};

const loadStoredMessages = (): Record<string, ChatMessageItem[]> => {
  if (typeof window === 'undefined') return INITIAL_DEMO_MESSAGES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MESSAGES);
    if (!raw) return INITIAL_DEMO_MESSAGES;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : INITIAL_DEMO_MESSAGES;
  } catch {
    return INITIAL_DEMO_MESSAGES;
  }
};

const saveStoredSessions = (sessions: ChatSessionItem[]) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY_SESSIONS, JSON.stringify(sessions));
    } catch {
      // Ignored
    }
  }
};

const saveStoredMessages = (messages: Record<string, ChatMessageItem[]>) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY_MESSAGES, JSON.stringify(messages));
    } catch {
      // Ignored
    }
  }
};

export const useChatStore = create<ChatState>((set, get) => {
  const initialSessions = loadStoredSessions();
  const initialMessages = loadStoredMessages();

  const initialActiveId =
    typeof window !== 'undefined'
      ? localStorage.getItem(STORAGE_KEY_ACTIVE_ID) || initialSessions[0]?.id || null
      : initialSessions[0]?.id || null;

  const initialPersonaId =
    typeof window !== 'undefined'
      ? localStorage.getItem(STORAGE_KEY_PERSONA) || SYSTEM_CHAT_PERSONAS[0].id
      : SYSTEM_CHAT_PERSONAS[0].id;

  const initialModelCode =
    typeof window !== 'undefined'
      ? localStorage.getItem(STORAGE_KEY_MODEL) || CHAT_AI_MODELS[0].code
      : CHAT_AI_MODELS[0].code;

  return {
    sessions: initialSessions,
    messages: initialMessages,
    activeSessionId: initialActiveId,
    activePersonaId: initialPersonaId,
    activeModelCode: initialModelCode,
    searchQuery: '',
    isSidebarOpen: true,
    isGenerating: false,
    activeCitationDrawer: null,
    isShareModalOpen: false,
    shareModalSessionId: null,

    createSession: (options) => {
      const { sessions, activePersonaId, activeModelCode } = get();
      const newId = generateId();
      const now = new Date().toISOString();

      const persona =
        SYSTEM_CHAT_PERSONAS.find((p) => p.id === (options?.personaId || activePersonaId)) ||
        SYSTEM_CHAT_PERSONAS[0];

      const model = options?.modelCode || persona.recommendedModel || activeModelCode;

      const newSession: ChatSessionItem = {
        id: newId,
        title: options?.title || `Cuộc trò chuyện với ${persona.name}`,
        personaId: persona.id,
        modelCode: model,
        isPinned: false,
        createdAt: now,
        updatedAt: now,
        totalMessages: 0,
      };

      const updated = [newSession, ...sessions];
      saveStoredSessions(updated);

      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_ACTIVE_ID, newId);
        localStorage.setItem(STORAGE_KEY_PERSONA, persona.id);
        localStorage.setItem(STORAGE_KEY_MODEL, model);
      }

      set({
        sessions: updated,
        activeSessionId: newId,
        activePersonaId: persona.id,
        activeModelCode: model,
      });

      return newId;
    },

    selectSession: (id: string) => {
      const { sessions } = get();
      const target = sessions.find((s) => s.id === id);
      if (!target) return;

      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_ACTIVE_ID, id);
        localStorage.setItem(STORAGE_KEY_PERSONA, target.personaId);
        localStorage.setItem(STORAGE_KEY_MODEL, target.modelCode);
      }

      set({
        activeSessionId: id,
        activePersonaId: target.personaId,
        activeModelCode: target.modelCode,
      });
    },

    updateSessionTitle: (id: string, title: string) => {
      const { sessions } = get();
      const trimmed = title.trim();
      if (!trimmed) return;

      const updated = sessions.map((s) =>
        s.id === id ? { ...s, title: trimmed, updatedAt: new Date().toISOString() } : s
      );

      saveStoredSessions(updated);
      set({ sessions: updated });
    },

    togglePinSession: (id: string) => {
      const { sessions } = get();
      const updated = sessions.map((s) =>
        s.id === id ? { ...s, isPinned: !s.isPinned } : s
      );

      saveStoredSessions(updated);
      set({ sessions: updated });
    },

    deleteSession: (id: string) => {
      const { sessions, activeSessionId, messages } = get();
      const updatedSessions = sessions.filter((s) => s.id !== id);

      const updatedMessages = { ...messages };
      delete updatedMessages[id];

      let nextActiveId = activeSessionId;
      if (activeSessionId === id) {
        nextActiveId = updatedSessions.length > 0 ? updatedSessions[0].id : null;
      }

      saveStoredSessions(updatedSessions);
      saveStoredMessages(updatedMessages);

      if (typeof window !== 'undefined') {
        if (nextActiveId) {
          localStorage.setItem(STORAGE_KEY_ACTIVE_ID, nextActiveId);
        } else {
          localStorage.removeItem(STORAGE_KEY_ACTIVE_ID);
        }
      }

      set({
        sessions: updatedSessions,
        messages: updatedMessages,
        activeSessionId: nextActiveId,
      });
    },

    incrementSessionMessages: (id: string) => {
      const { sessions } = get();
      const updated = sessions.map((s) =>
        s.id === id
          ? {
              ...s,
              totalMessages: s.totalMessages + 1,
              updatedAt: new Date().toISOString(),
            }
          : s
      );

      saveStoredSessions(updated);
      set({ sessions: updated });
    },

    setActivePersona: (personaId: string) => {
      const { sessions, activeSessionId } = get();
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_PERSONA, personaId);
      }

      let updatedSessions = sessions;
      if (activeSessionId) {
        updatedSessions = sessions.map((s) =>
          s.id === activeSessionId
            ? { ...s, personaId, updatedAt: new Date().toISOString() }
            : s
        );
        saveStoredSessions(updatedSessions);
      }

      set({
        activePersonaId: personaId,
        sessions: updatedSessions,
      });
    },

    setActiveModel: (modelCode: string) => {
      const { sessions, activeSessionId } = get();
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_MODEL, modelCode);
      }

      let updatedSessions = sessions;
      if (activeSessionId) {
        updatedSessions = sessions.map((s) =>
          s.id === activeSessionId
            ? { ...s, modelCode, updatedAt: new Date().toISOString() }
            : s
        );
        saveStoredSessions(updatedSessions);
      }

      set({
        activeModelCode: modelCode,
        sessions: updatedSessions,
      });
    },

    setSearchQuery: (searchQuery: string) => set({ searchQuery }),

    toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

    setSidebarOpen: (isSidebarOpen: boolean) => set({ isSidebarOpen }),

    setIsGenerating: (isGenerating: boolean) => set({ isGenerating }),

    addMessage: (sessionId, rawMsg) => {
      const newMsgId = rawMsg.id || generateId();
      const fullMessage: ChatMessageItem = {
        ...rawMsg,
        id: newMsgId,
        sessionId,
        createdAt: rawMsg.createdAt || new Date().toISOString(),
      };

      const { messages, sessions } = get();
      const sessionList = messages[sessionId] || [];
      const updatedList = [...sessionList, fullMessage];
      const updatedMessages = { ...messages, [sessionId]: updatedList };

      const updatedSessions = sessions.map((s) =>
        s.id === sessionId
          ? {
              ...s,
              totalMessages: updatedList.length,
              updatedAt: new Date().toISOString(),
            }
          : s
      );

      saveStoredMessages(updatedMessages);
      saveStoredSessions(updatedSessions);

      set({
        messages: updatedMessages,
        sessions: updatedSessions,
      });

      return newMsgId;
    },

    updateMessageContent: (sessionId, messageId, content, isStreaming = false) => {
      const { messages } = get();
      const list = messages[sessionId];
      if (!list) return;

      const updatedList = list.map((m) =>
        m.id === messageId ? { ...m, content, isStreaming } : m
      );
      const updatedMessages = { ...messages, [sessionId]: updatedList };

      saveStoredMessages(updatedMessages);
      set({ messages: updatedMessages });
    },

    appendMessageDelta: (sessionId, messageId, delta) => {
      const { messages } = get();
      const list = messages[sessionId];
      if (!list) return;

      const updatedList = list.map((m) =>
        m.id === messageId
          ? { ...m, content: m.content + delta, isStreaming: true }
          : m
      );
      const updatedMessages = { ...messages, [sessionId]: updatedList };

      set({ messages: updatedMessages });
    },

    finalizeMessage: (sessionId, messageId, meta) => {
      const { messages, sessions } = get();
      const list = messages[sessionId];
      if (!list) return;

      const updatedList = list.map((m) =>
        m.id === messageId
          ? {
              ...m,
              isStreaming: meta?.isStreaming ?? false,
              tokensUsed: meta?.tokensUsed ?? m.tokensUsed,
              creditCost: meta?.creditCost ?? m.creditCost,
              citations: meta?.citations ?? m.citations,
            }
          : m
      );
      const updatedMessages = { ...messages, [sessionId]: updatedList };

      saveStoredMessages(updatedMessages);

      const updatedSessions = sessions.map((s) =>
        s.id === sessionId ? { ...s, updatedAt: new Date().toISOString() } : s
      );
      saveStoredSessions(updatedSessions);

      set({
        messages: updatedMessages,
        sessions: updatedSessions,
        isGenerating: false,
      });
    },

    clearSessionMessages: (sessionId: string) => {
      const { messages, sessions } = get();
      const updatedMessages = { ...messages, [sessionId]: [] };

      const updatedSessions = sessions.map((s) =>
        s.id === sessionId ? { ...s, totalMessages: 0, updatedAt: new Date().toISOString() } : s
      );

      saveStoredMessages(updatedMessages);
      saveStoredSessions(updatedSessions);

      set({
        messages: updatedMessages,
        sessions: updatedSessions,
      });
    },

    rateMessage: (sessionId, messageId, rating, reason) => {
      const { messages } = get();
      const list = messages[sessionId];
      if (!list) return;

      const updatedList = list.map((m) =>
        m.id === messageId
          ? {
              ...m,
              feedback: rating ?? undefined,
              feedbackReason: reason ?? m.feedbackReason,
            }
          : m
      );
      const updatedMessages = { ...messages, [sessionId]: updatedList };

      saveStoredMessages(updatedMessages);
      set({ messages: updatedMessages });
    },

    openCitationDrawer: (messageId, selectedIndex, citations) => {
      set({
        activeCitationDrawer: {
          messageId,
          selectedIndex,
          citations,
        },
      });
    },

    closeCitationDrawer: () => {
      set({ activeCitationDrawer: null });
    },

    openShareModal: (sessionId) => {
      const { activeSessionId } = get();
      set({
        isShareModalOpen: true,
        shareModalSessionId: sessionId || activeSessionId,
      });
    },

    closeShareModal: () => {
      set({
        isShareModalOpen: false,
        shareModalSessionId: null,
      });
    },

    resetStore: () => {
      const initial = [...INITIAL_DEMO_SESSIONS];
      const initialMsgs = { ...INITIAL_DEMO_MESSAGES };
      saveStoredSessions(initial);
      saveStoredMessages(initialMsgs);

      set({
        sessions: initial,
        messages: initialMsgs,
        activeSessionId: initial[0].id,
        activePersonaId: SYSTEM_CHAT_PERSONAS[0].id,
        activeModelCode: CHAT_AI_MODELS[0].code,
        searchQuery: '',
        isSidebarOpen: true,
        isGenerating: false,
        activeCitationDrawer: null,
        isShareModalOpen: false,
        shareModalSessionId: null,
      });
    },

    getActiveSession: () => {
      const { sessions, activeSessionId } = get();
      return sessions.find((s) => s.id === activeSessionId);
    },

    getSessionMessages: (sessionId) => {
      const { messages, activeSessionId } = get();
      const targetId = sessionId || activeSessionId;
      if (!targetId) return [];
      return messages[targetId] || [];
    },

    getGroupedSessions: () => {
      const { sessions, searchQuery } = get();
      return groupSessionsByTime(sessions, searchQuery);
    },
  };
});
